import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import React, {useEffect, useRef, useState} from 'react';
import {ReactComponent as AddIcon} from '../../../src/assests/svg/add.svg';
import {ReactComponent as UpIcon} from '../../../src/assests/svg/chev_up.svg';
import {ReactComponent as DownIcon} from '../../../src/assests/svg/down_icon.svg';
import {ReactComponent as DropDownIcon} from '../../../src/assests/svg/drop_down.svg';
import {AurDropdownButton} from '../common/aur-button/AurDropdownButton';
import {AurButton} from '../common/aur-button/AurButton';
import UserDetailsView from '../user-profile/UserDetailsView';
import {IconColors, IconNames} from '../common/aur-icon/AurIcon';
import {TeamMemberStatus} from './user-management/models';
import {useDispatch, useSelector} from 'react-redux';
import {RootState, store} from '../../redux/store';
import {
  initailLabelDetails,
  labelProps,
} from '../user-management/aur-user-details-view/models';
import {setTeamMember} from '../../redux/slices/teamMemberSlice';
import {
  setChangeTracker,
  resetChangeTracker,
} from '../../redux/slices/globalChangeTrackerSlice';
import AurAccordionGroup from '../common/aur-accordion/AurAccordionGroup';
import {
  removeUserRole,
  setUserRolesData,
  upsertUserRole,
  UserRolesData,
} from '../../redux/slices/userRoleSlice';
import {UserRoleIds} from '../../types/user-management/userManagementTypes';
import {getUserRoles, deleteUserRole} from '../../api/ClientManagementService';
import {
  fetchUserDetailsByIds,
  getUserFindByEmail,
} from '../../api/UserManagementService';
import {UserRoleCode} from '../../types/user-role-codes/roleCodes';
import AurPopup from '../common/aur-popup/AurPopup';
import AurSvgIcon from '../common/aur-svg-icon/AurSvgIcon';
import ClientAndProjectFormContainer, {
  ClientDetailsInfo,
} from '../user-management/forms/ClientAndProjectFormContainer';
import {getUTCDateTimeForDisplay, isValidGuid} from '../../utils/utils';
import {UserDetail} from '../../types/types';
import AurSysAdminLabel from '../common/aur-sys-admin-label/AurSysAdminLabel';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import AurBreadcrumb from '../common/aur-breadcrumb/AurBreadCrumb';
import {getPermissions} from '../../redux/slices/permissionsSlice';
import {
  EDIT_TEAM_MEMBER,
  BUDGET_ESTIMATED,
  ONLY_DATE_SHOW_FORMAT,
} from '../../constants/constants';
import {Permission} from '../../types/permission-group/permission';
import {uniqBy} from 'lodash';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {useAnalytics} from '../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../constants/analyticsConstants';
import {clearNewlyAddedPermissionGroup} from '../../redux/slices/permissionGroupSlice';
import {selectCanAddClientToUser} from '../../utils/permissionsUtils';

type UserRoleWithAudit = UserRolesData & {
  createdBy?: string;
  updatedBy?: string;
  /** CM API may return these instead of createTime/updateTime */
  createdTime?: string;
  updatedTime?: string;
  createdDate?: string;
  updatedDate?: string;
};

const UserProfile: React.FC = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const {selectedUserEmailAddress, teamMemberInfo} = useSelector(
    (state: RootState) => state.teamMember
  );
  const storedUserRoles = useSelector(
    (state: RootState) => state.userRole.userRoles
  );
  const {showToast} = useToast();
  const [isEdit, setIsEdit] = useState<boolean>(false);

  // use this to render client list
  const [clientList, setClientList] = useState<ClientDetailsInfo[]>([]);

  const [userRolesLoaded, setUserRolesLoaded] = useState(false);
  const loadedProfileUserIdRef = useRef<string | null>(null);
  const wasSysAdminRef = useRef(false);
  const sysAdminDemotedPendingRef = useRef(false);
  const profileFetchGenerationRef = useRef(0);
  const [userListDisabled, setUserListDisabled] = useState<boolean>(false);

  const [isEdittingClient, setIsEdittingClient] = useState(false);
  const [openProjectClientId, setOpenProjectClientId] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number>(0);
  const [labelDetails, setLabelDetails] = useState<labelProps[]>([]);
  const [isFetchUserProfileDataLoading, setIsFetchUserProfileDataLoading] =
    useState(false);
  const [isGetUserByEmailLoading, setIsGetUserByEmailLoading] = useState(false);
  const [showHCProjectsCreate, setShowHCProjectsCreate] = useState(false);
  const [showHCProjectsEdit, setShowHCProjectsEdit] = useState(false);
  const permissionsFromDB = useSelector(getPermissions) || [];
  const [isEdittingProject, setIsEdittingProject] = useState(false);

  const [auditFields, setAuditFields] = useState<{
    createdByName?: string;
    updatedByName?: string;
    createdByDate?: string;
    updatedByDate?: string;
  }>({
    createdByName: '-',
    updatedByName: '-',
    createdByDate: '',
    updatedByDate: '',
  });
  const [, setAuditUsers] = useState<UserDetail[]>([]);

  const loggedInUserEmail = useSelector(
    (state: RootState) => state.user.user?.email
  );

  const showAddClientButton = useSelector(selectCanAddClientToUser);

  const extractAuditUserIds = (userRoles: UserRolesData[]): string[] => {
    const ids: string[] = [];
    userRoles.forEach((role) => {
      const item = role as UserRoleWithAudit;
      if (item.createdBy) ids.push(item.createdBy);
      if (item.updatedBy) ids.push(item.updatedBy);
    });
    return filterValidGuids([...new Set(ids)]);
  };

  const resolveAuditUserDisplayName = (
    userGuid: string | undefined,
    resolvedUsers: UserDetail[]
  ): string => {
    if (!userGuid) return '-';
    const user = resolvedUsers.find(
      (x) => x.userId.toLowerCase() === userGuid.toLowerCase()
    );
    if (user) {
      return `${user.firstName} ${user.lastName}`.trim();
    }
    return isValidGuid(userGuid) ? 'unknown' : userGuid;
  };

  const isSystemAdminRole = (userRole: UserRolesData): boolean =>
    userRole.role?.roleCode === UserRoleCode.systemAdminUserRole ||
    userRole.role?.roleId === UserRoleIds.SystemAdmin;

  const checkIfSystemAdmin = (userRoles: UserRolesData[]): boolean =>
    userRoles.some(isSystemAdminRole);

  const excludeSystemAdminRoles = (
    userRoles: UserRolesData[]
  ): UserRolesData[] => userRoles.filter((role) => !isSystemAdminRole(role));

  const hasSystemAdministratorRole = userRolesLoaded
    ? checkIfSystemAdmin(storedUserRoles)
    : null;

  const filterValidGuids = (strings: string[]): string[] => {
    return strings.filter(isValidGuid);
  };

  const mapUserRolesToClientList = (
    userRoles: UserRolesData[],
    resolvedUsers: UserDetail[]
  ): ClientDetailsInfo[] => {
    let isClientAdmin = false;
    return userRoles.map((item, index: number) => {
      const audit = item as UserRoleWithAudit;
      if (item.role.roleCode === 'CLIENT_ADMINISTRATOR') isClientAdmin = true;
      else isClientAdmin = false;
      return {
        id: index,
        isAdmin: isClientAdmin,
        createdBy: resolveAuditUserDisplayName(
          audit.createdBy ?? '',
          resolvedUsers
        ),
        createdDate: new Date(
          audit.createdTime ??
            audit.createdTime ??
            audit.createdDate ??
            Date.now()
        ),
        updatedBy: resolveAuditUserDisplayName(
          audit.updatedBy ?? '',
          resolvedUsers
        ),
        updatedDate: new Date(
          audit.updatedTime ??
            audit.updatedTime ??
            audit.updatedDate ??
            Date.now()
        ),
        isExpanded: false,
        client: item.client?.name,
        clientId: item.client?.clientId,
        budgetConsumptionEnabled:
          item?.client?.budgetConsumptionEnabled ?? false,
        role: item.role.name,
        roleId: item.role.roleId,
        userRoleId: item.userRoleId,
        permissionGroup:
          item.userRolePermissionGroup?.length > 0
            ? (item.userRolePermissionGroup[0].permissionGroup?.name as string)
            : undefined,
        permissionGroupId:
          item.userRolePermissionGroup?.length > 0
            ? (item.userRolePermissionGroup[0].permissionGroup
                ?.permissionGroupId as string)
            : undefined,
        projectList: (item.projectWorkspaceList ?? []).map((p: any) => {
          const projectPermissions = p.projectPermissions;
          const permissionCodeBudgetConsumption = projectPermissions?.some(
            (perm: Permission) =>
              perm.permissionCode === BUDGET_ESTIMATED.REPORT_BUDGET_CONSUMPTION
          );
          const permissionCodeBudgetAlert = projectPermissions?.some(
            (perm: Permission) =>
              perm.permissionCode === BUDGET_ESTIMATED.REPORT_BUDGET_ALERT
          );

          return {
            ...p,
            projectId: p.id,
            projectName: p.name,
            permissionGroupId: p.permissionGroupId ? p.permissionGroupId : null,
            highlyConfidential: p.highlyConfidential,
            domainCountryCode: p.domainCountryCode,
            dataCenterName: p.dataCenterName,
            countryName: p.countryName,
            projectPermissions: projectPermissions,
            permissionCodeBudgetConsumption,
            permissionCodeBudgetAlert,
            workspaces: uniqBy(p.workspaces ?? [], 'id').map((w: any) => ({
              workspaceId: w.id,
              workspaceName: w.name,
              workspaceCode: w.code,
            })),
          };
        }),
      };
    });
  };

  const resolveAuditUsersSafely = async (
    userRoles: UserRolesData[]
  ): Promise<UserDetail[]> => {
    try {
      return await fetchUserDetailsByIds(extractAuditUserIds(userRoles));
    } catch (error) {
      console.error('Failed to resolve audit user names:', error);
      return [];
    }
  };

  const clearClientAccessUI = () => {
    setClientList([]);
    setAuditUsers([]);
  };

  const rebuildClientListWithAuditNames = async (
    userRoles: UserRolesData[]
  ) => {
    const resolvedUsers = await resolveAuditUsersSafely(userRoles);
    setAuditUsers(resolvedUsers);
    setClientList(mapUserRolesToClientList(userRoles, resolvedUsers));
  };

  const syncReduxRolesToSysAdminOnly = () => {
    const roles = store.getState().userRole.userRoles;
    const sysAdminOnly = roles.filter(isSystemAdminRole);
    if (sysAdminOnly.length !== roles.length) {
      dispatch(setUserRolesData(sysAdminOnly));
    }
  };

  const handleSysAdminRoleChanged = async (
    change: 'promoted' | 'demoted'
  ): Promise<void> => {
    if (change === 'promoted') {
      sysAdminDemotedPendingRef.current = false;
      wasSysAdminRef.current = true;
      clearClientAccessUI();
      syncReduxRolesToSysAdminOnly();
      setUserRolesLoaded(true);
      return;
    }

    profileFetchGenerationRef.current += 1;
    sysAdminDemotedPendingRef.current = true;
    wasSysAdminRef.current = false;
    clearClientAccessUI();
    dispatch(setUserRolesData([]));
    setUserRolesLoaded(true);

    if (teamMemberInfo.userId) {
      await fetchUserProfileData(teamMemberInfo.userId);
    }
    sysAdminDemotedPendingRef.current = false;
  };

  const applySavedUserRole = async (savedRole: UserRolesData) => {
    dispatch(upsertUserRole(savedRole));
    setUserRolesLoaded(true);
    const mergedRoles = storedUserRoles.some(
      (role) => role.userRoleId === savedRole.userRoleId
    )
      ? storedUserRoles.map((role) =>
          role.userRoleId === savedRole.userRoleId ? savedRole : role
        )
      : [...storedUserRoles, savedRole];
    if (checkIfSystemAdmin(mergedRoles)) {
      clearClientAccessUI();
      wasSysAdminRef.current = true;
      dispatch(setUserRolesData(mergedRoles.filter(isSystemAdminRole)));
      return;
    }
    await rebuildClientListWithAuditNames(mergedRoles);
  };

  const removeUserRoleFromState = async (userRoleId: string) => {
    const removedRole = storedUserRoles.find(
      (role) => role.userRoleId === userRoleId
    );
    dispatch(removeUserRole(userRoleId));
    setUserRolesLoaded(true);
    const filteredRoles = storedUserRoles.filter(
      (role) => role.userRoleId !== userRoleId
    );
    const removedSysAdmin =
      removedRole?.role?.roleId === UserRoleIds.SystemAdmin ||
      removedRole?.role?.roleCode === UserRoleCode.systemAdminUserRole;

    if (removedSysAdmin) {
      await handleSysAdminRoleChanged('demoted');
      return;
    }

    if (checkIfSystemAdmin(filteredRoles)) {
      clearClientAccessUI();
      wasSysAdminRef.current = true;
      dispatch(setUserRolesData(filteredRoles.filter(isSystemAdminRole)));
      return;
    }
    await rebuildClientListWithAuditNames(filteredRoles);
  };

  const handleUserRoleSaved = async (savedUserRole?: UserRolesData) => {
    if (savedUserRole && teamMemberInfo.userId) {
      await applySavedUserRole(savedUserRole);
      return;
    }
    if (teamMemberInfo.userId) {
      fetchUserProfileData(teamMemberInfo.userId as string);
    }
  };

  const fetchUserProfileData = async (userId: string) => {
    const fetchId = ++profileFetchGenerationRef.current;
    setIsFetchUserProfileDataLoading(true);
    try {
      const result = await getUserRoles(userId);
      if (fetchId !== profileFetchGenerationRef.current) {
        return;
      }

      const userRolesRaw = result.data.data.value.userRoles as UserRolesData[];

      const currentRoles = store.getState().userRole.userRoles;
      const shouldStripStaleSysAdmin =
        sysAdminDemotedPendingRef.current ||
        (userRolesLoaded &&
          userId === teamMemberInfo.userId &&
          !checkIfSystemAdmin(currentRoles));
      const userRoles = shouldStripStaleSysAdmin
        ? excludeSystemAdminRoles(userRolesRaw)
        : userRolesRaw;

      if (fetchId !== profileFetchGenerationRef.current) {
        return;
      }

      dispatch(setUserRolesData(userRoles));
      setUserRolesLoaded(true);
      const isSysAdmin = checkIfSystemAdmin(userRoles);
      wasSysAdminRef.current = isSysAdmin;
      if (isSysAdmin) {
        clearClientAccessUI();
      } else {
        const resolvedUsers = await resolveAuditUsersSafely(userRoles);
        if (fetchId !== profileFetchGenerationRef.current) {
          return;
        }
        setAuditUsers(resolvedUsers);
        setClientList(mapUserRolesToClientList(userRoles, resolvedUsers));
      }
    } catch (error) {
      console.error('Error in fetchClients:', error);
    } finally {
      if (fetchId === profileFetchGenerationRef.current) {
        setIsFetchUserProfileDataLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!userRolesLoaded) {
      return;
    }

    const isSysAdminNow = checkIfSystemAdmin(storedUserRoles);

    if (isSysAdminNow) {
      clearClientAccessUI();
      wasSysAdminRef.current = true;
      syncReduxRolesToSysAdminOnly();
      return;
    }

    if (wasSysAdminRef.current && !sysAdminDemotedPendingRef.current) {
      void handleSysAdminRoleChanged('demoted');
      return;
    }

    let cancelled = false;
    (async () => {
      const clientRoles = excludeSystemAdminRoles(storedUserRoles);
      const resolvedUsers = await resolveAuditUsersSafely(clientRoles);
      if (cancelled) return;
      setAuditUsers(resolvedUsers);
      setClientList(mapUserRolesToClientList(clientRoles, resolvedUsers));
    })();

    return () => {
      cancelled = true;
    };
  }, [storedUserRoles, userRolesLoaded, teamMemberInfo.userId]);

  useEffect(() => {
    const userId = teamMemberInfo.userId;
    if (!userId) return;
    if (loadedProfileUserIdRef.current === userId) return;
    loadedProfileUserIdRef.current = userId;
    setUserRolesLoaded(false);
    fetchUserProfileData(userId);
  }, [teamMemberInfo.userId]);

  useEffect(() => {
    return () => {
      loadedProfileUserIdRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (teamMemberInfo.email) {
      if (teamMemberInfo.userId) {
        const invitedStatusLabelProps: labelProps = {
          status: t('common.editDetails.invited'),
          icon: IconNames.Email,
          iconColor: IconColors.Success,
          iconSize: 'small',
        };

        const tempLabelDetails: labelProps[] = [
          {
            status: teamMemberInfo.isActive
              ? t('status.active')
              : t('status.inactive'),
            icon: teamMemberInfo.isActive
              ? IconNames.Circle
              : IconNames.CircleEmpty,
            iconColor: teamMemberInfo.isActive
              ? IconColors.Success
              : IconColors.Secondary,
            iconSize: 'small',
            ...(teamMemberInfo?.status === TeamMemberStatus.invited &&
            !teamMemberInfo.isActive
              ? invitedStatusLabelProps
              : {}),
          },
          {
            status: teamMemberInfo.isBillable
              ? t('common.userProfile.billable')
              : t('common.userProfile.nonBillable'),
            icon: teamMemberInfo.isBillable
              ? IconNames.CurrencyUSD
              : IconNames.CurrencyUSDClose,
            iconColor: teamMemberInfo.isBillable
              ? IconColors.Success
              : IconColors.Secondary,
            iconSize: 'small',
          },
          {
            status: teamMemberInfo.isLimitIp ? t('status.limitedIPAccess') : '',
            icon: teamMemberInfo.isLimitIp ? IconNames.Security : undefined,
            iconColor: IconColors.Success,
            iconSize: 'small',
          },
        ];
        setLabelDetails(tempLabelDetails);
      }

      getUserFindByEmail(teamMemberInfo.email).then((res) => {
        setAuditFields({
          createdByDate: res.data.data.createdTime
            ? getUTCDateTimeForDisplay(
                res.data.data.createdTime,
                ONLY_DATE_SHOW_FORMAT
              )
            : '-',
          updatedByDate: res.data.data.updatedTime
            ? getUTCDateTimeForDisplay(
                res.data.data.updatedTime,
                ONLY_DATE_SHOW_FORMAT
              )
            : '-',
          createdByName: res.data.data.createdBy || '-',
          updatedByName: res.data.data.updatedBy || '-',
        });
      });
    }
  }, [teamMemberInfo]);

  const [clientListData, setClientListData] = useState<ClientDetailsInfo[]>(
    clientList.map((client) => ({
      ...client,
      isExpanded: clientList.length <= 6 ? true : false,
    }))
  );

  // Clean up change tracker state
  useEffect(() => {
    return () => {
      dispatch(resetChangeTracker());
    };
  }, []);

  useEffect(() => {
    if (clientList.length > 0) {
      setClientListData(
        clientList.map((client) => ({
          ...client,
          isExpanded: clientList.length <= 6 ? true : false,
        }))
      );
    } else setClientListData([]);
  }, [clientList]);

  useEffect(() => {
    const value =
      clientList[selectedClientId]?.projectList?.some(
        (p) => p.highlyConfidential
      ) ?? false;
    setShowHCProjectsEdit(value);
  }, [clientList, selectedClientId]);

  const [sortCriteria, setSortCriteria] = useState<string>('Newest First');
  const [isClientControlsDisabled, setIsClientControlsDisabled] =
    useState<boolean>(true);
  const [isAddingNewClient, setIsAddingNewClient] = useState(false);

  const areAllExpanded = clientList.every((item) => item.isExpanded);

  const breadCrumbItems = [
    {
      label: t('buttons.manageTeamMembers'),
      path: '/users',
    },
    {
      label: t('common.userProfile.teamMemberProfile'),
    },
  ];

  useEffect(() => {
    clientList.length < 6
      ? setIsClientControlsDisabled(true)
      : setIsClientControlsDisabled(false);
  }, []);

  const expandAll = () => {
    setClientListData(clientList.map((item) => ({...item, isExpanded: true})));
  };
  const collapseAll = () => {
    setClientListData(clientList.map((item) => ({...item, isExpanded: false})));
  };

  useEffect(() => {
    if (selectedUserEmailAddress) {
      setIsGetUserByEmailLoading(true);
      getUserFindByEmail(selectedUserEmailAddress)
        .then((res) => {
          const tempLabelDetails = [...initailLabelDetails];
          tempLabelDetails[0].status = teamMemberInfo.isActive
            ? t('status.active')
            : t('status.inactive');
          tempLabelDetails[0].icon =
            res.data.data.status == TeamMemberStatus.active
              ? IconNames.Circle
              : IconNames.CircleEmpty;
          tempLabelDetails[0].iconColor =
            res.data.data.status == TeamMemberStatus.active
              ? IconColors.Success
              : IconColors.Secondary;

          teamMemberInfo?.status === TeamMemberStatus.invited &&
            !teamMemberInfo.isActive &&
            (tempLabelDetails[0] = {
              status: t('status.invited'),
              icon: IconNames.Email,
              iconColor: IconColors.Success,
              iconSize: 'small',
            });

          tempLabelDetails[1].status = res.data.data.isBillable
            ? t('common.userProfile.billable')
            : t('common.userProfile.nonBillable');
          tempLabelDetails[1].icon = res.data.data.isBillable
            ? IconNames.CurrencyUSD
            : IconNames.CurrencyUSDClose;
          tempLabelDetails[1].iconColor = res.data.data.isBillable
            ? IconColors.Success
            : IconColors.Secondary;

          if (res.data.data.userIPWhiteList.length == 0) {
            tempLabelDetails.splice(2, 1);
          }

          setLabelDetails(tempLabelDetails);
          dispatch(
            setTeamMember({
              userId: res.data.data?.userId,
              firstName: res.data.data?.firstName,
              lastName: res.data.data?.lastName,
              email: res.data.data?.email,
              isActive: ![
                TeamMemberStatus.inactive,
                TeamMemberStatus.invited,
              ].includes(res.data.data?.status),
              status: res.data.data?.status,
              isBillable: res.data.data.isBillable,
              isLimitIp:
                res.data.data.userIPWhiteList.length === 0 ? false : true,
              ipAddressList: res.data.data.userIPWhiteList.map((item: any) =>
                item.ipAddress?.replace(/\s+/g, '')
              ),
            })
          );
          setAuditFields({
            createdByDate: res.data.data.createdTime
              ? getUTCDateTimeForDisplay(
                  res.data.data.createdTime,
                  ONLY_DATE_SHOW_FORMAT
                )
              : '-',
            updatedByDate: res.data.data.updatedTime
              ? getUTCDateTimeForDisplay(
                  res.data.data.updatedTime,
                  ONLY_DATE_SHOW_FORMAT
                )
              : '-',
            createdByName: res.data.data.createdBy || '-',
            updatedByName: res.data.data.updatedBy || '-',
          });
        })
        .finally(() => setIsGetUserByEmailLoading(false));
    }
  }, [selectedUserEmailAddress]);

  const sortButton = (
    <AurDropdownButton
      buttonText={sortCriteria}
      type="text"
      startIcon={<DropDownIcon aria-hidden="true" />}
      aria-live="polite"
      size="Small"
      iconType="Arrow"
      menuItemHeight="large"
      menuItemWidth="medium"
      menuItemTextSize="large"
      menuItems={[
        {
          label: t('sort.newestFirst'),
          onClick: () => {
            setSortCriteria(t('sort.newestFirst'));
          },
        },
        {
          label: t('sort.oldestFirst'),
          onClick: () => {
            setSortCriteria(t('sort.oldestFirst'));
          },
        },
        {
          label: t('sort.alphabetical'),
          onClick: () => {
            setSortCriteria(t('sort.alphabetical'));
          },
        },
        {
          label: t('sort.lastModified'),
          onClick: () => {
            setSortCriteria(t('sort.lastModified'));
          },
        },
      ]}
    />
  );

  const deleteClient = async () => {
    const userRoleIdToDelete = clientList[selectedClientId]
      ?.userRoleId as string;
    try {
      const {userRoleId: deletedUserRoleId} = await deleteUserRole(
        teamMemberInfo.userId as string,
        userRoleIdToDelete,
        {
          clientId: clientList[selectedClientId]?.clientId,
        }
      );
      await removeUserRoleFromState(deletedUserRoleId);
      setShowDeleteModal(false);
      showToast(
        t('addEditTeamMember.form.removeClientSuccess'),
        'success',
        undefined,
        'center'
      );
    } catch (e) {
      console.error(e);
      setShowDeleteModal(false);
      showToast(
        t('addEditTeamMember.form.removeClientFailed'),
        'error',
        undefined,
        'center'
      );
    }
  };

  const deleteClientConfirmation = async (id: number) => {
    setSelectedClientId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
  };

  const toggleButton = (
    <AurButton
      type="text"
      label={areAllExpanded ? t('buttons.collapseAll') : t('buttons.expandAll')}
      onClick={areAllExpanded ? collapseAll : expandAll}
      size="Small"
      startIcon={
        areAllExpanded ? (
          <UpIcon aria-hidden="true" />
        ) : (
          <DownIcon aria-hidden="true" />
        )
      }
    />
  );

  const titleContent = (
    <>
      {!isClientControlsDisabled && (
        <div className="title-controls">
          {sortButton}
          {toggleButton}
        </div>
      )}
      {loggedInUserEmail != selectedUserEmailAddress && showAddClientButton && (
        <AurButton
          type="secondary"
          label={t('common.userProfile.addNewClient')}
          onClick={() => {
            setIsAddingNewClient(true);
            dispatch(
              setChangeTracker({
                hasChange: true,
                modalTitle: t('common.userProfile.unsaved.title'),
                modalDescription:
                  t('common.userProfile.unsaved.description1') +
                  teamMemberInfo.firstName +
                  ' ' +
                  teamMemberInfo.lastName +
                  t('common.userProfile.unsaved.description2'),
                okButtonLabel: t('common.userProfile.unsaved.okButton'),
                closeButtonLabel: t('common.userProfile.unsaved.closeButton'),
                className: 'max-width-540',
              })
            );
          }}
          size="Medium"
          disabled={
            isAddingNewClient ||
            isEdittingClient ||
            Boolean(openProjectClientId)
          }
          startIcon={
            <AddIcon className="add-client-btn-icon" aria-hidden="true" />
          }
        />
      )}
    </>
  );

  const addClientGraphic = (
    <div className={userListDisabled ? 'add-new-div disabled' : 'add-new-div'}>
      <Box>
        <AurSvgIcon src="images/no_client_bg.svg" />
        <Typography className="typo-text headline2_size">
          {t('common.userProfile.noClient')}
        </Typography>
        <AurButton
          type="continue"
          label={t('common.userProfile.addNewClient')}
          onClick={() => {
            setIsAddingNewClient(true);
            dispatch(
              setChangeTracker({
                hasChange: true,
                modalTitle: t('common.userProfile.unsaved.title'),
                modalDescription:
                  t('common.userProfile.unsaved.description1') +
                  teamMemberInfo.firstName +
                  ' ' +
                  teamMemberInfo.lastName +
                  t('common.userProfile.unsaved.description2'),
                okButtonLabel: t('common.userProfile.unsaved.okButton'),
                closeButtonLabel: t('common.userProfile.unsaved.closeButton'),
                className: 'max-width-540',
              })
            );
          }}
          size="Medium"
          startIcon={<AddIcon aria-hidden="true" />}
        />
      </Box>
    </div>
  );

  const trackEditClick = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.editClient'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.users.editClient'),
      },
    });
  };

  const addClientForm = (
    <ClientAndProjectFormContainer
      clientDetailsInfo={{
        client: '',
        clientId: '',
        permissionGroup: '',
        permissionGroupId: '',
        projectList: [],
        role: '',
        roleId: '',
        budgetConsumptionEnabled: false,
      }}
      setShowHCProjects={setShowHCProjectsCreate}
      showHCProjects={showHCProjectsCreate}
      savedClientList={clientList}
      teamMemberId={teamMemberInfo.userId}
      teamMemberEmail={teamMemberInfo.email}
      permissionFromDB={permissionsFromDB}
      mode="create"
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onDeleteButtonClicked={(_id: number) => {}}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onEditButtonClicked={(_id: number) => {}}
      onSaveButtonClicked={(savedUserRole) => {
        setIsAddingNewClient(false);
        dispatch(resetChangeTracker());
        handleUserRoleSaved(savedUserRole);
      }}
      onCancelButtonClicked={() => {
        setIsAddingNewClient(false);
        dispatch(resetChangeTracker());
      }}
    />
  );

  const editClientForm = (
    <ClientAndProjectFormContainer
      clientDetailsInfo={clientList[selectedClientId]}
      teamMemberId={teamMemberInfo.userId}
      teamMemberEmail={teamMemberInfo.email}
      permissionFromDB={permissionsFromDB}
      mode="edit"
      onDeleteButtonClicked={(id: number) => {
        console.log(id);
      }}
      setShowHCProjects={setShowHCProjectsEdit}
      showHCProjects={showHCProjectsEdit}
      onEditButtonClicked={() => {
        setIsEdittingClient(true);
        setSelectedClientId(-1);
      }}
      onSaveButtonClicked={(savedUserRole) => {
        setIsEdittingClient(false);
        setSelectedClientId(-1);
        dispatch(resetChangeTracker());
        handleUserRoleSaved(savedUserRole);
      }}
      onCancelButtonClicked={() => {
        setIsEdittingClient(false);
        setSelectedClientId(-1);
        dispatch(resetChangeTracker());
        fetchUserProfileData(teamMemberInfo.userId as string);
      }}
      defaultSelectedClientId={clientList[selectedClientId]?.clientId}
    />
  );
  const clientListContent = clientListData.map((clientDetail, index) => {
    var isHCGranted: boolean | undefined = false;
    if (
      clientDetail.isAdmin &&
      clientDetail?.projectList &&
      clientDetail?.projectList?.length > 0
    ) {
      isHCGranted = clientDetail?.projectList?.some(
        (p) => p.highlyConfidential
      );
    }
    if (isEdittingClient && selectedClientId === index) return editClientForm;
    else
      return (
        <ClientAndProjectFormContainer
          key={clientDetail.clientId}
          isControlDisabled={
            isAddingNewClient ||
            isEdittingClient ||
            (isEdittingProject && selectedClientId !== index) ||
            (openProjectClientId != '' &&
              openProjectClientId != clientDetail.clientId)
          }
          clientDetailsInfo={clientDetail}
          teamMemberId={teamMemberInfo.userId}
          teamMemberEmail={teamMemberInfo.email}
          permissionFromDB={permissionsFromDB}
          mode="view"
          showHCProjects={isHCGranted}
          onDeleteButtonClicked={(id: number) => {
            deleteClientConfirmation(id);
          }}
          onEditButtonClicked={(id: number) => {
            dispatch(
              setChangeTracker({
                hasChange: true,
                modalTitle: t('common.userProfile.unsaved.title'),
                modalDescription:
                  t('common.userProfile.unsaved.description1') +
                  teamMemberInfo.firstName +
                  ' ' +
                  teamMemberInfo.lastName +
                  t('common.userProfile.unsaved.description2'),
                okButtonLabel: t('common.userProfile.unsaved.okButton'),
                closeButtonLabel: t('common.userProfile.unsaved.closeButton'),
                className: 'max-width-540',
              })
            );
            setIsEdittingClient(true);
            setSelectedClientId(id);
            trackEditClick();
          }}
          onEditProject={(id: number, isEdit: boolean) => {
            setIsEdittingProject(isEdit);
            setSelectedClientId(id);
          }}
          onProjectSavedOrCanceled={(savedUserRole) => {
            dispatch(resetChangeTracker());
            setOpenProjectClientId('');
            if (savedUserRole) {
              handleUserRoleSaved(savedUserRole);
            }
            dispatch(clearNewlyAddedPermissionGroup());
          }}
        />
      );
  });

  const clientListPanel = () => {
    let newClientForm = <></>;
    if (isAddingNewClient) {
      // Show the new client form if we are adding a new client
      newClientForm = addClientForm;
    }

    let existingClientList: any = <></>;
    if (clientListData.length) {
      // Show the list of existing clients if any are assigned
      existingClientList = clientListContent;
    }

    let newClientGraphic = <></>;
    const noClientsAssigned = !clientListData.length;
    const isNotAddingNewClient = !isAddingNewClient;
    if (noClientsAssigned && isNotAddingNewClient) {
      // Only show the new client panel if:
      // 1. No clients are assigned
      // 2. We are not currently adding a client
      newClientGraphic = addClientGraphic;
    }

    return (
      <div>
        <AurAccordionGroup
          className="aur-user-client-list"
          title={t('common.editDetails.clientList')}
          titleContent={titleContent}
          fullWidth
          disabled={userListDisabled}
        >
          <>
            {newClientForm}
            {existingClientList}
            {newClientGraphic}
          </>
        </AurAccordionGroup>
      </div>
    );
  };

  return (
    <>
      <Box className="user-profile-main">
        <div className="user-details-bread-crumb-container">
          <AurBreadcrumb items={breadCrumbItems} />
        </div>

        <div className="user-details-main-container-margin">
          <UserDetailsView
            userId={teamMemberInfo.userId ?? ''}
            userFirstName={teamMemberInfo.firstName}
            userLastName={teamMemberInfo.lastName}
            userEmail={teamMemberInfo.email}
            userListDisabled={userListDisabled}
            setUserListDisabled={setUserListDisabled}
            onSysAdminRoleChanged={handleSysAdminRoleChanged}
            flowName={EDIT_TEAM_MEMBER}
            labelDetails={labelDetails}
            createdByName={auditFields.createdByName}
            updatedByName={auditFields.updatedByName}
            createdByDate={auditFields.createdByDate}
            updatedByDate={auditFields.updatedByDate}
            onEditChange={setIsEdit}
          />
        </div>
        {!isEdit && hasSystemAdministratorRole == true && (
          <div className="user-profile-sysadmin-label-container">
            <AurSysAdminLabel />
          </div>
        )}
        {hasSystemAdministratorRole == false && clientListPanel()}
      </Box>
      {showDeleteModal && (
        <AurPopup
          className="profile-delete-modal aur-unsaved-changes-popup"
          open={showDeleteModal}
          onOk={deleteClient}
          onCancel={handleDeleteModalClose}
          onClose={handleDeleteModalClose}
          popupTitle={t('common.userProfile.deletePopup.title')}
          popupHeading={t('common.userProfile.deletePopup.heading')}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
          popupContent={t(
            clientListData.length === 1
              ? 'common.userProfile.deletePopup.singleClientContent'
              : 'common.userProfile.deletePopup.multipleClientContent'
          )}
          labelActionCancel={t('common.userProfile.deletePopup.cancelBtn')}
          labelActionOk={t('common.userProfile.deletePopup.deleteBtn')}
        />
      )}
      {
        <AurLoadingIndicator
          overlay={true}
          isOpen={isFetchUserProfileDataLoading || isGetUserByEmailLoading}
        />
      }
    </>
  );
};

export default UserProfile;
